import React from "react";
import { Link } from "react-router-dom";
import {Shield, Users, Car, Home, Truck, AlertCircle} from 'lucide-react';

const CoveragePage = () => {
  const coverageOptions = [
    {
      icon: Users,
      title: "Racer Liability Coverage",
      description:
        "We cover the racer, not just the car. Protection for bodily injury and property damage liability during sanctioned racing events.",
      features: [
        "Bodily injury liability",
        "Property damage coverage",
        "Legal defense costs",
        "Medical payments",
      ],
    },
    {
      icon: Car,
      title: "Physical Damage Coverage",
      description:
        "Comprehensive and collision coverage for your high-value modified drag racing vehicle, both on and off the track.",
      features: [
        "On-track damage protection",
        "Collision coverage",
        "Comprehensive coverage",
        "Agreed value policies",
      ],
    },
    {
      icon: Home,
      title: "Off-Track Storage",
      description:
        "Protection for your vehicle when it's not racing. Coverage for theft, fire, vandalism, and other perils while in storage.",
      features: [
        "Garage storage coverage",
        "Theft protection",
        "Fire and vandalism",
        "Weather damage",
      ],
    },
    {
      icon: Truck,
      title: "Transport & Trailer Coverage",
      description:
        "Insurance for drag racing cars while being transported to and from events, including trailer coverage.",
      features: [
        "In-transit protection",
        "Trailer physical damage",
        "Equipment coverage",
        "Nationwide protection",
      ],
    },
    {
      icon: Shield,
      title: "Spare Parts & Equipment",
      description:
        "Coverage for your expensive racing equipment, spare parts, and tools that are essential to your racing operation.",
      features: [
        "Engine and transmission spares",
        "Racing equipment",
        "Tools and accessories",
        "Custom parts coverage",
      ],
    },
    {
      icon: AlertCircle,
      title: "Track Day Coverage",
      description:
        "Specialized insurance for drag racing cars during practice sessions, test days, and time trials at the track.",
      features: [
        "Practice session coverage",
        "Time trial protection",
        "Test day insurance",
        "Flexible policy terms",
      ],
    },
  ];

  return (
    <div className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#1a1a1a] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-[#DC0000]">Insurance for Drag Racers</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              We cover the racer, not just the car. Comprehensive drag racers
              insurance tailored to your unique needs, from the strip to the
              garage.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-[#DC0000] text-white px-8 py-4 rounded-md font-semibold text-lg hover:bg-red-700 transition-all transform hover:scale-105"
            >
              Get Your Coverage Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Why Specialized Insurance */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Why You Need Specialized{" "}
              <span className="text-[#DC0000]">Drag Racers Insurance</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-gray-300">
              <div>
                <p className="text-lg mb-4">
                  Standard auto insurance policies typically exclude coverage for
                  racing activities. If you're involved in an incident at the
                  drag strip, your regular car insurance won't protect you.
                </p>
                <p className="text-lg">
                  That's where specialized insurance for drag racing cars comes
                  in. We understand the unique risks and high values associated
                  with modified drag racing vehicles.
                </p>
              </div>
              <div>
                <p className="text-lg mb-4">
                  Our drag race car insurance policies are designed specifically
                  for racers, covering everything from sanctioned events to
                  off-track storage and transport.
                </p>
                <p className="text-lg">
                  Whether you're a weekend warrior or a semi-pro competitor, we
                  have coverage options that protect your investment and give you
                  peace of mind.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Options */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Comprehensive{" "}
              <span className="text-[#DC0000]">Coverage Options</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Flexible drag racers insurance policies designed to protect every
              aspect of your racing operation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coverageOptions.map((option, index) => (
              <div
                key={index}
                className="bg-black p-8 rounded-lg border border-gray-800 hover:border-[#DC0000] transition-all"
              >
                <option.icon className="w-12 h-12 text-[#DC0000] mb-4" />
                <h3 className="text-2xl font-semibold mb-3">{option.title}</h3>
                <p className="text-gray-400 mb-6">{option.description}</p>
                <ul className="space-y-2">
                  {option.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-gray-300">
                      <span className="text-[#DC0000] mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Cover */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                What Our{" "}
                <span className="text-[#DC0000]">Drag Race Car Insurance</span>{" "}
                Covers
              </h2>
              <div className="space-y-6 text-gray-300">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Sanctioned Racing Events
                  </h3>
                  <p>
                    Coverage during NHRA, IHRA, and other sanctioned drag racing
                    events. Protection when you're doing what you love most.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Modified Vehicles
                  </h3>
                  <p>
                    Unlike standard policies, we cover high-value modified
                    vehicles with agreed value coverage options.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Transport & Storage
                  </h3>
                  <p>
                    Your vehicle is covered while being transported to events and
                    during off-season storage.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Equipment & Parts
                  </h3>
                  <p>
                    Coverage extends to your expensive spare parts, racing
                    equipment, and tools.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-[#1a1a1a] p-8 rounded-lg border border-gray-800">
              <h3 className="text-2xl font-bold mb-6">Coverage Highlights</h3>
              <ul className="space-y-4">
                {[
                  "Liability coverage up to $1M+",
                  "Agreed value policies for modified vehicles",
                  "No depreciation on total loss claims",
                  "Coverage during practice and qualifying",
                  "Spare parts and equipment protection",
                  "Trailer and transport vehicle coverage",
                  "Off-track storage protection",
                  "24/7 claims support",
                  "Nationwide coverage",
                  "Flexible payment options",
                ].map((highlight, index) => (
                  <li key={index} className="flex items-start">
                    <Shield className="w-5 h-5 text-[#DC0000] mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#DC0000]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
            Get Your Drag Racers Insurance Quote Today
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Protect your investment with specialized insurance for drag racing
            cars. Fast quotes, affordable rates, expert service.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-black text-white px-8 py-4 rounded-md font-semibold text-lg hover:bg-gray-900 transition-all transform hover:scale-105"
          >
            Request a Quote
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CoveragePage;
